// Worker 管理モジュール
//
// 責務:
// - textlint Web Worker の遅延ロード（最初の lint 要求時まで何もしない）
// - 複数 textarea からの同時リクエストを ID で多重化
// - debounce ユーティリティの提供
// - エラー時の Worker 再生成
//
// 設計上のポイント:
//   shanyang は Vue 2 + Vuex (一部) で動いていて、Composition API はない。
//   そのためプレーン ES module として書き、各ディレクティブから直接 import する。

// 開発時は毎モジュール評価時に tag を付けてブラウザの HTTP cache を回避する。
// public/ 配下のファイルは webpack HMR で更新検知されないため、
// この cache-bust を入れないと古い Worker が読まれ続ける。
// 本番ビルドでは process.env.NODE_ENV === "production" なので固定 URL が返る。
const IS_DEV =
  typeof process !== 'undefined' &&
  process.env &&
  process.env.NODE_ENV !== 'production'
const WORKER_URL = IS_DEV
  ? '/static/workers/textlint.worker.js?t=' + Date.now()
  : '/static/workers/textlint.worker.js'
const WORKER_LOAD_TIMEOUT_MS = 15000

let workerInstance = null
let workerReadyPromise = null
let nextRequestId = 1
const pendingRequests = new Map()

function createWorker() {
  return new Promise((resolve, reject) => {
    let worker
    try {
      worker = new Worker(WORKER_URL)
    } catch (err) {
      reject(err)
      return
    }

    const timer = setTimeout(() => {
      try {
        worker.terminate()
      } catch (e) {
        /* noop */
      }
      reject(new Error('textlint worker load timeout'))
    }, WORKER_LOAD_TIMEOUT_MS)

    worker.onmessage = (e) => {
      const data = e.data || {}
      if (data.id === '__ready__') {
        clearTimeout(timer)
        // 以降のメッセージハンドラに差し替え
        worker.onmessage = onWorkerMessage
        resolve(worker)
      }
      // ready 前に来たメッセージはハンドリング対象外
    }

    worker.onerror = (err) => {
      clearTimeout(timer)
      reject(err)
    }
  })
}

function onWorkerMessage(e) {
  const data = e.data || {}
  const { id, ok, messages, error } = data
  if (typeof id === 'undefined') return
  const handler = pendingRequests.get(id)
  if (!handler) return
  pendingRequests.delete(id)
  if (ok) {
    handler.resolve(messages || [])
  } else {
    handler.reject(new Error(error || 'textlint failed'))
  }
}

function ensureWorker() {
  if (workerInstance) return Promise.resolve(workerInstance)
  if (workerReadyPromise) return workerReadyPromise

  workerReadyPromise = createWorker()
    .then((worker) => {
      workerInstance = worker
      workerInstance.onerror = (err) => {
        // Worker が死んだら次回ロード時に作り直す
        console.warn('[proofread] worker error, will recreate', err)
        resetWorker()
      }
      return worker
    })
    .catch((err) => {
      workerReadyPromise = null
      throw err
    })

  return workerReadyPromise
}

function resetWorker() {
  if (workerInstance) {
    try {
      workerInstance.terminate()
    } catch (e) {
      /* noop */
    }
  }
  workerInstance = null
  workerReadyPromise = null
  // pending を全て reject（UI 側で握り潰される）
  for (const [, handler] of pendingRequests) {
    handler.reject(new Error('worker reset'))
  }
  pendingRequests.clear()
}

/**
 * テキストを lint して messages 配列を返す。
 * Worker 未ロードなら初回呼び出し時にロードする（遅延ロード）。
 * @param {string} text
 * @returns {Promise<Array>}
 */
export async function lintText(text) {
  if (!text || !text.trim()) return []

  let worker
  try {
    worker = await ensureWorker()
  } catch (err) {
    console.warn('[proofread] worker unavailable:', err)
    return []
  }

  return new Promise((resolve, reject) => {
    const id = nextRequestId++
    pendingRequests.set(id, { resolve, reject })
    try {
      worker.postMessage({ id, text })
    } catch (err) {
      pendingRequests.delete(id)
      reject(err)
    }
  })
}

/**
 * debounce ユーティリティ。
 * @param {Function} fn
 * @param {number} ms
 */
export function debounce(fn, ms) {
  let timer = null
  const debounced = function (...args) {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      timer = null
      fn.apply(this, args)
    }, ms)
  }
  debounced.cancel = () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }
  return debounced
}

// テスト・デバッグ用
export const __internal__ = {
  resetWorker,
  getWorker: () => workerInstance
}
